import React, { useState, useEffect, useRef } from 'react';
import { Device } from '@twilio/voice-sdk';
import {
    Phone,
    PhoneOff,
    Loader2,
    AlertCircle,
    XCircle,
    Building2
} from 'lucide-react';
import { callService } from '../../services/callService';
import { useParams } from 'react-router-dom';
import { Entreprise } from '../../types/features/Entreprise';
import { entrepriseService } from '../../services/entrepriseService';

type CallStatus = 'idle' | 'connecting' | 'connected' | 'ended' | 'error';

export const WebRTCCallPage: React.FC = () => {
    const { id: entrepriseId } = useParams<{ id: string }>();

    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [callStatus, setCallStatus] = useState<CallStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [callDuration, setCallDuration] = useState(0);
    const [company, setCompany] = useState<Entreprise | null>(null);
    const [isLoadingCompany, setIsLoadingCompany] = useState(true);
    const [companyError, setCompanyError] = useState<string | null>(null);
    const deviceRef = useRef<Device | null>(null);
    const callRef = useRef<any>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const startTimer = () => {
        setCallDuration(0);
        timerRef.current = setInterval(() => {
            setCallDuration(prev => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const resetCallState = () => {
        setCallStatus('idle');
        setCallDuration(0);
        setErrorMessage('');
        stopTimer();
        callRef.current = null;

        if (deviceRef.current) {
            deviceRef.current.destroy();
            deviceRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            if (deviceRef.current) {
                deviceRef.current.destroy();
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                setIsLoadingCompany(true);
                const entreprise = await entrepriseService.getPublicEntrepriseById(entrepriseId!);
                setCompany(entreprise);
            } catch (err) {
                setCompanyError('Entreprise non trouvée');
            } finally {
                setIsLoadingCompany(false);
            }
        };

        if (entrepriseId) {
            fetchCompany();
        }
    }, [entrepriseId]);

    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleStartCall = async () => {
        if (!email) {
            setErrorMessage('Veuillez entrer votre adresse email');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setErrorMessage('Veuillez entrer une adresse email valide');
            return;
        }

        if (!entrepriseId) {
            setErrorMessage('Veillez utiliser un lien valide');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setCallStatus('connecting');

        try {
            const result = await callService.getWebRTCToken(entrepriseId, email);

            if (!result.success) {
                throw new Error('Erreur lors de la récupération du token');
            }

            const { token } = result.data;

            const device = new Device(token, {
                logLevel: 1
            });
            deviceRef.current = device;

            device.on('error', (err) => {
                console.error('Erreur Twilio Device:', err);
                setCallStatus('error');
                setErrorMessage(err.message || 'Erreur du device');
                setIsLoading(false);
            });

            const call = await device.connect({
                params: {
                    entreprise_id: entrepriseId
                }
            });
            callRef.current = call;

            call.on('accept', () => {
                setCallStatus('connected');
                startTimer();
                setIsLoading(false);
            });

            call.on('disconnect', () => {
                console.log('Appel déconnecté');
                stopTimer();
                setCallStatus('ended');
                setTimeout(resetCallState, 3000);
            });

            call.on('cancel', () => {
                console.log('Appel annulé');
                stopTimer();
                setCallStatus('ended');
                setTimeout(resetCallState, 3000);
            });

            call.on('reject', () => {
                console.log('Appel rejeté');
                stopTimer();
                setCallStatus('ended');
                setTimeout(resetCallState, 3000);
            });

            call.on('error', (error: any) => {
                console.error('Erreur appel:', error);
                setCallStatus('error');
                setErrorMessage(error.message || 'Erreur lors de l\'appel');
                setIsLoading(false);
                stopTimer();
            });

        } catch (error: any) {
            setCallStatus('error');
            setErrorMessage(error.message || 'Une erreur est survenue');
            setIsLoading(false);
        }
    };

    const handleHangup = () => {
        if (callRef.current) {
            try {
                callRef.current.disconnect();
            } catch (error) {
                console.error('Erreur lors du raccrochage:', error);
            }
        }
        resetCallState();
    };

    // Chargement de l'entreprise
    if (isLoadingCompany) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full shadow-xl text-center">
                    <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
                    <p className="mt-4 text-slate-500">Chargement...</p>
                </div>
            </div>
        );
    }

    if (companyError || !company) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full shadow-xl text-center">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
                    <h2 className="text-xl font-bold text-slate-900 mt-4">Entreprise non trouvée</h2>
                    <p className="text-slate-500 mt-2">{companyError}</p>
                </div>
            </div>
        );
    }

    // État : Prêt
    if (callStatus === 'idle') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full shadow-xl">
                    <div className="flex flex-col items-center text-center">
                        {/* Logo de l'entreprise */}
                        {company?.logo ? (
                            <img
                                src={company.logo}
                                alt={company.name}
                                className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-slate-200"
                            />
                        ) : (
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                <Building2 className="w-10 h-10 text-primary" />
                            </div>
                        )}

                        <h1 className="text-2xl font-bold text-slate-900 mb-1">
                            {company?.name || 'Support client'}
                        </h1>
                        <p className="text-slate-500 text-sm mb-6">
                            Entrez votre email pour contacter le support
                        </p>

                        <div className="w-full space-y-4">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="votre@email.com"
                                disabled={isLoading}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all disabled:opacity-60"
                                onKeyDown={(e) => e.key === 'Enter' && handleStartCall()}
                            />

                            {errorMessage && (
                                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-4 py-3 rounded-xl">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>{errorMessage}</span>
                                </div>
                            )}

                            <button
                                onClick={handleStartCall}
                                disabled={isLoading}
                                className="w-full flex items-center justify-center gap-3 bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-4 rounded-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Connexion...
                                    </>
                                ) : (
                                    <>
                                        <Phone className="w-5 h-5" />
                                        Appeler
                                    </>
                                )}
                            </button>

                            <p className="text-xs text-slate-400">
                                Appel sécurisé • Sans téléphone • Gratuit
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // État : En cours
    if (callStatus === 'connecting' || callStatus === 'connected') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full shadow-xl">
                    <div className="flex flex-col items-center text-center">
                        {/* Logo de l'entreprise pendant l'appel */}
                        {company?.logo ? (
                            <img
                                src={company.logo}
                                alt={company.name}
                                className="w-16 h-16 rounded-full object-cover mb-4 border-2 border-slate-200"
                            />
                        ) : (
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                <Building2 className="w-8 h-8 text-slate-400" />
                            </div>
                        )}

                        <p className="text-sm font-medium text-slate-900 mb-1">
                            {company?.name}
                        </p>

                        <div className="mb-6">
                            {callStatus === 'connecting' && (
                                <div className="relative w-20 h-20">
                                    <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
                                    <div className="absolute inset-0 border-4 border-primary rounded-full animate-ping"></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Phone className="w-8 h-8 text-primary" />
                                    </div>
                                </div>
                            )}
                            {callStatus === 'connected' && (
                                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center">
                                    <Phone className="w-8 h-8 text-green-600" />
                                </div>
                            )}
                        </div>

                        <div className="space-y-1">
                            <p className="text-sm font-medium text-slate-500">
                                {callStatus === 'connecting' ? 'Appel en cours...' : 'En communication'}
                            </p>
                            {callStatus === 'connected' && (
                                <p className="text-3xl font-bold text-slate-900 font-mono">
                                    {formatDuration(callDuration)}
                                </p>
                            )}
                        </div>

                        <button
                            onClick={handleHangup}
                            className="mt-8 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                        >
                            <PhoneOff className="w-5 h-5" />
                            Raccrocher
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // État : Terminé
    if (callStatus === 'ended') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full shadow-xl">
                    <div className="flex flex-col items-center text-center">
                        {company?.logo ? (
                            <img
                                src={company.logo}
                                alt={company.name}
                                className="w-16 h-16 rounded-full object-cover mb-4 border-2 border-slate-200"
                            />
                        ) : (
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                <Building2 className="w-8 h-8 text-slate-400" />
                            </div>
                        )}

                        <p className="text-sm font-medium text-slate-900 mb-1">
                            {company?.name}
                        </p>

                        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                            <XCircle className="w-10 h-10 text-slate-400" />
                        </div>

                        <h2 className="text-xl font-bold text-slate-900 mb-1">
                            Appel terminé
                        </h2>
                        {callDuration > 0 && (
                            <p className="text-slate-500 text-sm mb-6">
                                Durée : {formatDuration(callDuration)}
                            </p>
                        )}

                        <button
                            onClick={resetCallState}
                            className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                        >
                            <Phone className="w-5 h-5" />
                            Nouvel appel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // État : Erreur
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
            <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full shadow-xl">
                <div className="flex flex-col items-center text-center">
                    {company?.logo ? (
                        <img
                            src={company.logo}
                            alt={company.name}
                            className="w-16 h-16 rounded-full object-cover mb-4 border-2 border-slate-200"
                        />
                    ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                            <Building2 className="w-8 h-8 text-slate-400" />
                        </div>
                    )}

                    <p className="text-sm font-medium text-slate-900 mb-1">
                        {company?.name}
                    </p>

                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-4">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>

                    <h2 className="text-xl font-bold text-slate-900 mb-2">
                        Une erreur est survenue
                    </h2>
                    <p className="text-slate-500 text-sm mb-6">
                        {errorMessage}
                    </p>

                    <button
                        onClick={resetCallState}
                        className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                    >
                        <Phone className="w-5 h-5" />
                        Réessayer
                    </button>
                </div>
            </div>
        </div>
    );
};